import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, Alert, AlertTitle, Divider,
  LinearProgress, Tooltip, Button, Collapse, IconButton, alpha, useTheme,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SETTING_ACTIONS, ACTION_CATEGORIES } from '../../../config/tradingPresets';

// ============================================================================
// SUMMARY STAT CARD
// ============================================================================
function StatCard({ label, value, subtitle, color, icon, trend }) {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
        <Typography variant="h5" fontWeight="bold" color={color || 'text.primary'}>
          {value}
        </Typography>
        {trend && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {trend > 0 ? (
              <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
            ) : (
              <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main' }} />
            )}
          </Box>
        )}
      </Box>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
      )}
    </Paper>
  );
}

// ============================================================================
// QUICK ACTIONS (redesigned — grouped by Risk / Signals / Exits)
// ============================================================================
const QUICK_ACTION_GROUPS = [
  {
    label: 'Risk',
    icon: 'shield',
    actions: ['daily_profit_cap', 'daily_loss_cap', 'reduce_risk_safe_mode', 'max_concurrent_limit'],
  },
  {
    label: 'Signals',
    icon: 'sensors',
    actions: ['buy_only', 'sell_only', 'sell_filter_tightening', 'anti_chop_mode', 'low_rate_limit'],
  },
  {
    label: 'Exits',
    icon: 'gps_fixed',
    actions: ['aggressive_profit_lock', 'cooldown_booster', 'disable_rescue', 'rescue_conservative'],
  },
];

function QuickActionsPanel({ scheduleData, onActionToggle }) {
  const theme = useTheme();
  const activeActions = scheduleData?.activeActions || [];

  return (
    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
        Quick Actions
      </Typography>

      {QUICK_ACTION_GROUPS.map(group => (
        <Box key={group.label} sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
            {group.icon} {group.label}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {group.actions.map(actionId => {
              const action = SETTING_ACTIONS.find(a => a.id === actionId);
              if (!action) return null;
              const isActive = activeActions.includes(actionId);
              return (
                <Tooltip
                  key={actionId}
                  title={
                    <Box>
                      <Typography variant="caption" fontWeight="bold">{action.name}</Typography>
                      <Typography variant="caption" display="block">{action.description}</Typography>
                      <Typography variant="caption" display="block" color="warning.main">
                        {action.actionType === 'param_patch' ? 'Changes parameters directly' : 'Sets operational flag'}
                      </Typography>
                    </Box>
                  }
                  arrow
                >
                  <Chip
                    label={action.name}
                    icon={<span style={{ fontSize: 14 }}>{action.icon}</span>}
                    size="small"
                    variant={isActive ? 'filled' : 'outlined'}
                    color={isActive ? 'primary' : 'default'}
                    onClick={() => onActionToggle?.(actionId)}
                    sx={{
                      fontSize: '0.7rem',
                      height: 28,
                      '& .MuiChip-icon': { ml: 0.5 },
                    }}
                  />
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      ))}

      <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
        <Button size="small" variant="outlined" color="inherit" sx={{ fontSize: '0.7rem', textTransform: 'none' }}>
          Reset to Mode Defaults
        </Button>
        <Button size="small" variant="outlined" color="primary" sx={{ fontSize: '0.7rem', textTransform: 'none' }}>
          Apply to All Strategies
        </Button>
      </Box>
    </Paper>
  );
}

// ============================================================================
// MAIN OVERVIEW TAB
// ============================================================================
export default function OverviewTab({
  strategyId, strategyData, scheduleData, rejectionData, positionsData, modeInfo, onRefresh,
}) {
  const theme = useTheme();

  // Compute summary stats from positions
  const stats = useMemo(() => {
    if (!positionsData || positionsData.length === 0) {
      return {
        winRate: '—', avgWin: '—', avgLoss: '—', tradesToday: 0,
        tradesWeek: 0, profitFactor: '—', maxDrawdown: '—', avgFeeImpact: '—',
      };
    }

    const closed = positionsData.filter(p => p.status === 'closed' || p.closedAt);
    const wins = closed.filter(p => (p.realizedPnlPercent || p.pnlPercent || 0) > 0);
    const losses = closed.filter(p => (p.realizedPnlPercent || p.pnlPercent || 0) <= 0);

    const winRate = closed.length > 0 ? ((wins.length / closed.length) * 100).toFixed(1) : '—';
    const avgWin = wins.length > 0
      ? (wins.reduce((s, p) => s + (p.realizedPnlPercent || p.pnlPercent || 0), 0) / wins.length).toFixed(2) + '%'
      : '—';
    const avgLoss = losses.length > 0
      ? (losses.reduce((s, p) => s + Math.abs(p.realizedPnlPercent || p.pnlPercent || 0), 0) / losses.length).toFixed(2) + '%'
      : '—';

    const totalWin = wins.reduce((s, p) => s + (p.realizedPnlPercent || p.pnlPercent || 0), 0);
    const totalLoss = Math.abs(losses.reduce((s, p) => s + (p.realizedPnlPercent || p.pnlPercent || 0), 0));
    const profitFactor = totalLoss > 0 ? (totalWin / totalLoss).toFixed(2) : totalWin > 0 ? '∞' : '—';

    const now = Date.now();
    const dayMs = 86400000;
    const weekMs = 7 * dayMs;
    const tradesToday = closed.filter(p => now - new Date(p.closedAt || p.createdAt).getTime() < dayMs).length;
    const tradesWeek = closed.filter(p => now - new Date(p.closedAt || p.createdAt).getTime() < weekMs).length;

    return { winRate, avgWin, avgLoss, tradesToday, tradesWeek, profitFactor, maxDrawdown: '—', avgFeeImpact: '—' };
  }, [positionsData]);

  // Live status from rejection data
  const liveStatus = useMemo(() => {
    if (!rejectionData || rejectionData.error) {
      return { signalsFound: 0, qualified: 0, blocked: 0, entered: 0, topBlockers: [], regime: '—', loadout: '—' };
    }
    const topBlockers = [];
    const rejections = rejectionData.rejections || [];
    const gateCounts = {};
    rejections.forEach(r => { gateCounts[r.gate] = (gateCounts[r.gate] || 0) + 1; });
    Object.entries(gateCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .forEach(([gate, count]) => {
        const sample = rejections.find(r => r.gate === gate);
        topBlockers.push({ gate, count, reason: sample?.reason || '' });
      });

    const ctx = rejectionData.marketContext || {};
    return {
      signalsFound: rejectionData.totalSignals || 0,
      qualified: rejectionData.qualified || 0,
      blocked: rejections.length,
      entered: rejectionData.entries || 0,
      topBlockers,
      regime: ctx.regime || '—',
      loadout: ctx.loadout || '—',
      htfBias: ctx.htfBias || '—',
      adx: ctx.adx || '—',
      timestamp: rejectionData.timestamp,
    };
  }, [rejectionData]);

  const openPositions = positionsData.filter(p => !p.closedAt && p.status !== 'closed');

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            label="Win Rate (Net)"
            value={stats.winRate === '—' ? '—' : `${stats.winRate}%`}
            color={parseFloat(stats.winRate) >= 50 ? 'success.main' : parseFloat(stats.winRate) > 0 ? 'warning.main' : 'text.primary'}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard label="Avg Win" value={stats.avgWin} color="success.main" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard label="Avg Loss" value={stats.avgLoss} color="error.main" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard label="Trades (Day / Week)" value={`${stats.tradesToday} / ${stats.tradesWeek}`} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard label="Profit Factor" value={stats.profitFactor} color={parseFloat(stats.profitFactor) >= 1.5 ? 'success.main' : 'text.primary'} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard label="Open Positions" value={openPositions.length} />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* Left: What's Happening Now */}
        <Grid item xs={12} md={7}>
          <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="subtitle2" fontWeight="bold">What's Happening Now</Typography>
              {liveStatus.timestamp && (
                <Typography variant="caption" color="text.secondary">
                  Last scan: {new Date(liveStatus.timestamp).toLocaleTimeString()}
                </Typography>
              )}
            </Box>

            {/* Market Regime */}
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
              <Chip
                label={`Regime: ${liveStatus.regime}`}
                size="small"
                variant="outlined"
                color={liveStatus.regime === 'strong_trend' ? 'success' : liveStatus.regime === 'ranging' ? 'warning' : 'default'}
              />
              <Chip label={`HTF: ${liveStatus.htfBias}`} size="small" variant="outlined" />
              <Chip label={`ADX: ${liveStatus.adx}`} size="small" variant="outlined" />
              <Chip label={`Loadout: ${liveStatus.loadout}`} size="small" variant="outlined" />
              {modeInfo && (
                <Chip
                  label={modeInfo.profileName}
                  size="small"
                  color={modeInfo.isMode1 ? 'warning' : 'info'}
                  variant="filled"
                />
              )}
            </Box>

            {/* Signal Funnel Summary */}
            <Box sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={600}>{liveStatus.signalsFound}</Typography>
                  <Typography variant="caption" color="text.secondary">signals</Typography>
                </Box>
                <Typography color="text.disabled">→</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={600}>{liveStatus.qualified}</Typography>
                  <Typography variant="caption" color="text.secondary">qualified</Typography>
                </Box>
                <Typography color="text.disabled">→</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={600} color="error.main">{liveStatus.blocked}</Typography>
                  <Typography variant="caption" color="text.secondary">blocked</Typography>
                </Box>
                <Typography color="text.disabled">→</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={600} color="success.main">{liveStatus.entered}</Typography>
                  <Typography variant="caption" color="text.secondary">entered</Typography>
                </Box>
              </Box>
              <LinearProgress
                variant="determinate"
                value={liveStatus.signalsFound > 0 ? (liveStatus.entered / liveStatus.signalsFound) * 100 : 0}
                sx={{ height: 4, borderRadius: 2, bgcolor: alpha(theme.palette.text.disabled, 0.1) }}
              />
            </Box>

            {/* Top Blockers */}
            {liveStatus.qualified > 0 && liveStatus.entered === 0 && (
              <Alert severity="warning" variant="outlined" sx={{ mb: 1 }}>
                <AlertTitle sx={{ fontSize: '0.8rem', mb: 0.5 }}>No entries despite {liveStatus.qualified} qualified signals</AlertTitle>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {liveStatus.topBlockers.map((b, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BlockIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                      <Typography variant="caption">
                        <strong>{b.gate}</strong> ({b.count}×): {b.reason.substring(0, 80)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Alert>
            )}

            {liveStatus.entered > 0 && (
              <Alert severity="success" variant="outlined" icon={<CheckCircleIcon fontSize="small" />}>
                <Typography variant="caption">
                  {liveStatus.entered} position(s) opened this scan cycle
                </Typography>
              </Alert>
            )}

            {liveStatus.signalsFound === 0 && (
              <Alert severity="info" variant="outlined">
                <Typography variant="caption">
                  No signals generated in last scan. Market may be quiet or outside trading hours.
                </Typography>
              </Alert>
            )}
          </Paper>

          {/* Open Positions Summary */}
          {openPositions.length > 0 && (
            <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Open Positions</Typography>
              {openPositions.slice(0, 5).map((pos, i) => {
                const pnl = pos.pnlPercent || 0;
                return (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75, borderBottom: i < openPositions.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                    <Chip label={pos.side?.toUpperCase()} size="small" color={pos.side === 'buy' ? 'success' : 'error'} sx={{ width: 48, fontSize: '0.65rem' }} />
                    <Typography variant="body2" fontWeight={500} sx={{ minWidth: 80 }}>{pos.symbol}</Typography>
                    <Typography variant="caption" color="text.secondary">{pos.leverage || 5}×</Typography>
                    <Box sx={{ flex: 1 }} />
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color={pnl > 0 ? 'success.main' : pnl < 0 ? 'error.main' : 'text.primary'}
                    >
                      {pnl > 0 ? '+' : ''}{pnl.toFixed(2)}%
                    </Typography>
                    {pos.trailingActive && (
                      <Chip label="Trail" size="small" variant="outlined" color="info" sx={{ fontSize: '0.6rem', height: 20 }} />
                    )}
                    {pos.zeroLockActive && (
                      <Chip label="0-Lock" size="small" variant="outlined" color="success" sx={{ fontSize: '0.6rem', height: 20 }} />
                    )}
                  </Box>
                );
              })}
            </Paper>
          )}
        </Grid>

        {/* Right: Quick Actions */}
        <Grid item xs={12} md={5}>
          <QuickActionsPanel scheduleData={scheduleData} />
        </Grid>
      </Grid>
    </Box>
  );
}
